import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Pencil, Search, X, UserCheck, Shield, RefreshCw, Power, AlertCircle } from 'lucide-react';
import Table, { TABLE_THEME, StatusPill } from '../components/Table/Table';
import {
  fetchAccessPrivilegesByEmployee,
  fetchSysApplications,
  addAccessPrivileges,
  clearPrivilegeError,
  clearPrivilegeSuccess,
} from '../store/slices/privilegeSlice';
import Toast from '../components/Toast/Toast';
import useToast from '../utils/useToast';
import { Autocomplete, TextField } from '@mui/material'; // searchable application dropdown

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

const DEFAULT_FORM = {
  userId: '4',
  applicationId: '',
  roleType: 'U',
  accessRights: 'E',
  status: 'A',
};

export default function UserManagement() {
  const dispatch = useDispatch();
  const { toast, showToast, hideToast } = useToast();

  // Redux State
  const {
    accessPrivileges,
    accessPrivilegesLoading,
    sysApplications,
    saving,
    error,
    success,
  } = useSelector((state) => state.privilege);

  // Local State
  const [selectedUserId, setSelectedUserId] = useState('4');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  const closeModal = React.useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
    setForm(DEFAULT_FORM);
    dispatch(clearPrivilegeError());
  }, [dispatch]);

  // Fetch initial data
  useEffect(() => {
    dispatch(fetchSysApplications());
    if (selectedUserId) {
      dispatch(fetchAccessPrivilegesByEmployee(selectedUserId));
    }
  }, [dispatch, selectedUserId]);

  // Handle toast notifications
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      dispatch(clearPrivilegeError());
    }
    if (success) {
      showToast('Privilege saved successfully!', 'success');
      dispatch(clearPrivilegeSuccess());
      closeModal();
      // Refresh list
      if (selectedUserId) {
        dispatch(fetchAccessPrivilegesByEmployee(selectedUserId));
      }
    }
  }, [error, success, dispatch, selectedUserId, closeModal, showToast]);

  // Filter privileges by text search query
  const filteredPrivileges = useMemo(() => {
    if (!searchQuery.trim()) return accessPrivileges || [];
    const q = searchQuery.toLowerCase().trim();
    return (accessPrivileges || []).filter((item) => {
      const appId = String(item.AMV_APPLICATION_ID || '').toLowerCase();
      const appDesc = String(item.AMV_DESCRIPTION || '').toLowerCase();
      const username = String(item.AMV_USERNAME || '').toLowerCase();
      const serviceNo = String(item.AMV_SERVICE_NO || '').toLowerCase();
      const appName = String(item.AMV_APPLICATION || '').toLowerCase();
      return (
        appId.includes(q) ||
        appDesc.includes(q) ||
        username.includes(q) ||
        serviceNo.includes(q) ||
        appName.includes(q)
      );
    });
  }, [accessPrivileges, searchQuery]);

  const handleUserSearch = (e) => {
    e.preventDefault();
    if (selectedUserId.trim()) {
      dispatch(fetchAccessPrivilegesByEmployee(selectedUserId.trim()));
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setForm({
      ...DEFAULT_FORM,
      userId: selectedUserId || '4',
      applicationId: sysApplications.length > 0 ? sysApplications[0].ASA_APPLICATION_ID : '',
    });
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      userId: item.AMV_USER_ID || selectedUserId || '4',
      applicationId: item.AMV_APPLICATION_ID || '',
      roleType: item.AMV_ROLE_TYPE || 'U',
      accessRights: item.AMV_ACCESS_LEVEL || 'E',
      status: item.AMV_IS_ACTIVE === 'I' || item.AMV_IS_ACTIVE === 'Inactive' || item.AMV_IS_ACTIVE === 'False' ? 'I' : 'A',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.userId.trim()) {
      showToast('Please enter a User ID.', 'error');
      return;
    }
    if (!form.applicationId) {
      showToast('Please select an Application.', 'error');
      return;
    }

    const payload = {
      userId: form.userId.trim(),
      applicationId: form.applicationId,
      roleType: form.roleType,
      accessRights: form.accessRights,
      status: form.status,
    };

    if (editingItem && editingItem.AMV_PRIVILEGE_ID) {
      payload.privilegeId = editingItem.AMV_PRIVILEGE_ID;
    }

    dispatch(addAccessPrivileges(payload));
  };

  const handleToggleStatus = (item) => {
    const currentStatus = item.AMV_IS_ACTIVE === 'I' || item.AMV_IS_ACTIVE === 'Inactive' || item.AMV_IS_ACTIVE === 'False' ? 'I' : 'A';
    const newStatus = currentStatus === 'A' ? 'I' : 'A';
    
    const payload = {
      userId: item.AMV_USER_ID || selectedUserId,
      applicationId: item.AMV_APPLICATION_ID,
      roleType: item.AMV_ROLE_TYPE || 'U',
      accessRights: item.AMV_ACCESS_LEVEL || 'E',
      status: newStatus,
    };

    if (item.AMV_PRIVILEGE_ID) {
      payload.privilegeId = item.AMV_PRIVILEGE_ID;
    }

    dispatch(addAccessPrivileges(payload));
  };

  // Table columns
  const columns = [
    {
      key: 'AMV_PRIVILEGE_ID',
      label: 'Privilege ID',
      className: 'font-mono text-xs text-emerald-700 font-semibold',
      render: (r) => r.AMV_PRIVILEGE_ID || '-',
    },
    // {
    //   key: 'AMV_USER_ID',
    //   label: 'User ID',
    //   className: 'font-semibold text-gray-900',
    // },
    {
      key: 'AMV_USERNAME',
      label: 'Username',
      className: 'text-gray-700',
      render: (r) => r.AMV_USERNAME || 'admin',
    },
    {
      key: 'AMV_SERVICE_NO',
      label: 'Service No',
      className: 'text-gray-600',
      render: (r) => r.AMV_SERVICE_NO || '-',
    },
    {
      key: 'AMV_APPLICATION_ID',
      label: 'App ID',
      className: 'text-gray-500 font-mono text-xs',
    },
    {
      key: 'AMV_DESCRIPTION',
      label: 'Application Description',
      className: 'font-medium text-gray-900',
      render: (r) => r.AMV_DESCRIPTION || r.AMV_APPLICATION || '-',
    },
    // {
    //   key: 'AMV_APPLICATION_TYPE',
    //   label: 'Type',
    //   className: 'text-gray-500 text-xs uppercase',
    //   render: (r) => r.AMV_APPLICATION_TYPE || 'WB',
    // },
    {
      key: 'AMV_ROLE_TYPE',
      label: 'Role Type',
      align: 'center',
      render: (r) => {
        const isAdmin = r.AMV_ROLE_TYPE === 'A' || r.AMV_ROLE_TYPE === 'Admin';
        return (
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
              isAdmin
                ? 'bg-purple-100 text-purple-700 border border-purple-200'
                : 'bg-blue-100 text-blue-700 border border-blue-200'
            }`}
          >
            <Shield size={12} />
            {isAdmin ? 'Admin (A)' : 'User (U)'}
          </span>
        );
      },
    },
    {
      key: 'AMV_ACCESS_LEVEL',
      label: 'Access Rights',
      align: 'center',
      render: (r) => {
        const isEntry = r.AMV_ACCESS_LEVEL === 'E';
        return (
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
              isEntry
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'bg-amber-100 text-amber-700 border border-amber-200'
            }`}
          >
            {isEntry ? 'Entry / Edit / View (E)' : 'View Only (V)'}
          </span>
        );
      },
    },
    {
      key: 'AMV_IS_ACTIVE',
      label: 'Status',
      align: 'center',
      render: (r) => {
        const isActive =
          r.AMV_IS_ACTIVE !== 'I' &&
          r.AMV_IS_ACTIVE !== 'Inactive' &&
          r.AMV_IS_ACTIVE !== 'False';
        return (
          <StatusPill
            label={isActive ? 'Active' : 'Inactive'}
            tone={isActive ? 'success' : 'neutral'}
          />
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      render: (r) => {
        const isActive =
          r.AMV_IS_ACTIVE !== 'I' &&
          r.AMV_IS_ACTIVE !== 'Inactive' &&
          r.AMV_IS_ACTIVE !== 'False';
        return (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => openEditModal(r)}
              title="Edit Privilege"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => handleToggleStatus(r)}
              title={isActive ? 'Deactivate Privilege' : 'Activate Privilege'}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors ${
                isActive
                  ? 'border-red-200 text-red-600 hover:bg-red-50'
                  : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
              }`}
            >
              <Power size={13} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="text-emerald-600" size={24} />
            User Management & Access Control
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Manage system applications, user privilege assignments, role types, and access rights.
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="flex shrink-0 items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <Plus size={16} />
          Add User Privilege
        </button>
      </div>

      {/* ── Error Banner (Page level) ─────────────────────────────────── */}
      {error && !modalOpen && (
        <div className="rounded-lg bg-red-50 p-3.5 text-sm text-red-700 border border-red-200 flex items-center justify-between shadow-sm animate-fade-in">
          <div className="flex items-center gap-2">
            <AlertCircle size={18} className="text-red-600 shrink-0" />
            <span className="font-semibold">Error:</span> {error}
          </div>
          <button
            onClick={() => dispatch(clearPrivilegeError())}
            className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── Controls Strip (User ID Fetch + Search Filter) ───────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <form onSubmit={handleUserSearch} className="flex items-end gap-3 min-w-[280px]">
          <div>
            <span className={LABEL}>Fetch By User ID</span>
            <input
              type="text"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              placeholder="e.g. 4"
              className={`${FIELD} w-36`}
            />
          </div>
          <button
            type="submit"
            disabled={accessPrivilegesLoading}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-gray-800 text-white text-xs font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={accessPrivilegesLoading ? 'animate-spin' : ''} />
            Load Privileges
          </button>
        </form>

        <div className="flex-1 max-w-md">
          <span className={LABEL}>Filter List</span>
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by app ID, description, username..."
              className={`${FIELD} w-full pl-9`}
            />
          </div>
        </div>
      </div>

      {/* ── Privileges Table ────────────────────────────────────────── */}
      <Table
        columns={columns}
        rows={filteredPrivileges}
        rowKey={(r) => `${r.AMV_USER_ID}-${r.AMV_APPLICATION_ID}`}
        loading={accessPrivilegesLoading}
        loadingMessage="Loading access privileges..."
        emptyMessage={
          accessPrivileges.length === 0
            ? `No access privileges found for User ID '${selectedUserId}'.`
            : 'No access privileges match your search criteria.'
        }
        minWidth={800}
        pageSize={10}
      />

      {/* ── Add / Edit Modal ────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl border border-gray-100">
            <div className="mb-5 flex items-center justify-between border-b pb-3 border-gray-100">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Shield size={18} className="text-emerald-600" />
                {editingItem ? 'Edit User Access Privilege' : 'Add New User Access Privilege'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 rounded-lg p-1 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 p-3.5 text-sm text-red-700 border border-red-200 flex items-center justify-between shadow-sm animate-fade-in">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={17} className="text-red-600 shrink-0" />
                    <span>{error}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => dispatch(clearPrivilegeError())}
                    className="text-red-500 hover:text-red-700 p-0.5 transition-colors"
                  >
                    <X size={15} />
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className={LABEL}>User ID *</span>
                  <input
                    type="text"
                    value={form.userId}
                    disabled={Boolean(editingItem)}
                    onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
                    placeholder="e.g. 4"
                    required
                    className={`${FIELD} w-full ${editingItem ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-75' : ''}`}
                  />
                </div>

                <div>
                  <span className={LABEL}>System Application *</span>
                  {/* OLD native select (kept for reference):
                  <select
                    value={form.applicationId}
                    disabled={Boolean(editingItem)}
                    onChange={(e) => setForm((f) => ({ ...f, applicationId: e.target.value }))}
                    required
                    className={`${FIELD} w-full ${editingItem ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-75' : ''}`}
                  >
                    <option value="">Select Application</option>
                    {sysApplications.map((app) => (
                      <option key={app.ASA_APPLICATION_ID} value={app.ASA_APPLICATION_ID}>
                        [{app.ASA_APPLICATION_ID}] {app.ASA_DESCRIPTION}
                      </option>
                    ))}
                  </select>
                  */}
                  <Autocomplete
                    size="small"
                    options={sysApplications}
                    disabled={Boolean(editingItem)}
                    value={
                      sysApplications.find(
                        (a) => String(a.ASA_APPLICATION_ID) === String(form.applicationId)
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      setForm((f) => ({
                        ...f,
                        applicationId: newValue ? String(newValue.ASA_APPLICATION_ID) : '',
                      }))
                    }
                    getOptionLabel={(o) =>
                      o ? `[${o.ASA_APPLICATION_ID}] ${o.ASA_DESCRIPTION ?? ''}` : ''
                    }
                    isOptionEqualToValue={(o, v) =>
                      String(o.ASA_APPLICATION_ID) === String(v.ASA_APPLICATION_ID)
                    }
                    // search by application id OR description
                    filterOptions={(options, { inputValue }) => {
                      const t = inputValue.toLowerCase().trim();
                      if (!t) return options;
                      return options.filter(
                        (o) =>
                          String(o.ASA_APPLICATION_ID ?? '').toLowerCase().includes(t) ||
                          String(o.ASA_DESCRIPTION ?? '').toLowerCase().includes(t)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search application" />
                    )}
                    // match the native FIELD look (h-9, text-xs, rounded-lg, gray-200 border)
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 36,
                        fontSize: 12,
                        color: '#374151',
                        backgroundColor: editingItem ? '#f3f4f6' : '#fff',
                        borderRadius: '8px',
                        padding: '0 30px 0 10px !important',
                        opacity: editingItem ? 0.75 : 1,
                        '& fieldset': { borderColor: '#e5e7eb', borderWidth: 1 },
                        '&:hover fieldset': { borderColor: '#e5e7eb' },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgb(var(--color-primary-500))',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                        padding: '0 !important',
                        height: '100%',
                        fontSize: 12,
                        WebkitTextFillColor: editingItem ? '#6b7280' : undefined,
                      },
                      '& .MuiAutocomplete-endAdornment': { right: 6 },
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                    ListboxProps={{ sx: { fontSize: 12 } }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className={LABEL}>Role Type *</span>
                  <select
                    value={form.roleType}
                    disabled={Boolean(editingItem)}
                    onChange={(e) => setForm((f) => ({ ...f, roleType: e.target.value }))}
                    className={`${FIELD} w-full ${editingItem ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-75' : ''}`}
                  >
                    <option value="U">User (U)</option>
                    <option value="A">Admin (A)</option>
                  </select>
                </div>

                <div>
                  <span className={LABEL}>Access Rights *</span>
                  <select
                    value={form.accessRights}
                    disabled={Boolean(editingItem)}
                    onChange={(e) => setForm((f) => ({ ...f, accessRights: e.target.value }))}
                    className={`${FIELD} w-full ${editingItem ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-75' : ''}`}
                  >
                    <option value="E">Entry / Edit (E)</option>
                    <option value="V">View Only (V)</option>
                  </select>
                </div>
              </div>

              <div>
                <span className={LABEL}>Status</span>
                <div className="flex items-center gap-6 pt-1">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      value="A"
                      checked={form.status === 'A'}
                      onChange={() => setForm((f) => ({ ...f, status: 'A' }))}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    Active (A)
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      value="I"
                      checked={form.status === 'I'}
                      onChange={() => setForm((f) => ({ ...f, status: 'I' }))}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    Inactive (I)
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
                >
                  {saving && <RefreshCw size={14} className="animate-spin" />}
                  {saving ? 'Saving...' : editingItem ? 'Update Privilege' : 'Save Privilege'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={hideToast}
      />
    </div>
  );
}